import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '../store';

// ── "A new version is ready": updating an installed app without installing it again (report 0e8dd0cb) ────────────────
// The report: "Allow updates of all apps, files/email/chat to be done without asking the user to remove the app and
// reinstall in their phone. Perhaps a prompt in their app could work."
// An installed app stays open for days: it goes on running the JavaScript it loaded, it has no address bar to reload
// with, and nothing tells it a deploy happened, so reinstalling looked like the only way to the new screens. Its service
// worker (public/sw.js) stores nothing and has no say in this. A fresh load is always the newest build, because the page
// itself is served no-store (ops/nginx-site.conf.example). What was missing is something to ask for that fresh load.
// So Chat, Files and Email each compare the main.<hash>.js this page loaded with against the one the same address names
// now: as the app opens, whenever it comes back to the front, and every CHECK_MS. A different one draws the bar, and its
// Update button reloads the page. Only that press reloads, never a timer, as a half-written email or message would go
// with it. No hash at all (the dev server's bundle.js) means no bar.
// This reaches what the page draws: code and screens. An icon and an app's name are the phone's own copy, taken as it
// was installed, which no page can reach; on an iPhone a change to them still needs the icon added again.
const CHECK_MS = 5 * 60_000;
export const BUILD_FILE = /\/static\/js\/(main\.[a-f0-9]+\.js)/;   // as deploy.sh reads a live page's build

/** The build this page is running, from its own script tag. '' on the dev server, which has no hash. */
export function loadedBuild(): string {
  const found = Array.from(document.scripts).map(s => BUILD_FILE.exec(s.src)).find(Boolean);
  return found ? found[1] : '';
}

/** Should the bar be drawn? A newer build is on the server, and loading it now loses nothing. */
export function useAppUpdate(): boolean {
  const [newer, setNewer] = useState(false);
  // An email waiting out its Undo send seconds waits in this page, and a reload posts nothing: it stays in Drafts, its
  // sender believing it gone (components/UndoSend), and an iPhone never shows the "Leave site?" that guards it on a
  // desk. So the bar waits for it too, twenty seconds at the most.
  const sending = useStore(s => s.sendQueue.length > 0);

  useEffect(() => {
    const loaded = loadedBuild();
    if (!loaded) return;
    let live = true;
    const check = () => {
      if (document.visibilityState !== 'visible') return;   // coming back to the front asks at once
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistration().then(r => r?.update()).catch(() => { /* the next check asks again */ });
      }
      // The address alone: /chat?open=<job> is the same page. A page naming no build (an error page) says nothing.
      fetch(window.location.pathname, { cache: 'no-store' })
        .then(res => (res.ok ? res.text() : ''))
        .then(html => {
          const now = BUILD_FILE.exec(html);
          if (live && now) setNewer(now[1] !== loaded);
        })
        .catch(() => { /* offline: the next check asks again */ });
    };
    check();
    // A phone's timers do not run while the app is away, so the interval alone would miss a whole morning's deploy.
    const timer = window.setInterval(check, CHECK_MS);
    document.addEventListener('visibilitychange', check);
    return () => {
      live = false;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', check);
    };
  }, []);

  return newer && !sending;
}

/** The bar itself, across the top of the app's own screen: it moves the screen down and covers none of its buttons.
 *  What stands over that screen (an open email, a composer, a document) stands over the bar as well, so it never
 *  interrupts. */
export function AppUpdateBar() {
  const { t } = useTranslation();
  return (
    <div role="status" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderBottom: '1px solid var(--accent)', background: 'var(--accent-bg)', color: 'var(--accent-text)', flexShrink: 0 }}>
      <i className="ti ti-refresh" style={{ fontSize: 18, flexShrink: 0 }} aria-hidden />
      <div style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600 }}>{t('app_update_ready')}</div>
      <button onClick={() => window.location.reload()}
        style={{ padding: '6px 14px', border: 'none', borderRadius: 'var(--radius-md)', background: 'var(--accent)', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>
        {t('app_update_now')}
      </button>
    </div>
  );
}
